/**
 * The two FEOT project-target formulas, transcribed from feot-lambdas
 * helpers/feot.js (calculateProjectCT, updateQuotasCT) as recorded in FEOT v4
 * sections 3.1 / 3.4 / 10.2. Pure on purpose: unit tested against the
 * deterministic cases A-I rather than against a running lambda.
 */

const HUNDRED = 100;

export interface IProjectionInputs {
  goal: number;
  fielded: number;
  /** Planned field window, already in pacing units. */
  plannedFieldTime: number;
  /** now - surveyLaunchTS in pacing units. The MIDWAY clock. */
  elapsedFieldTime: number;
  /** Sum of fftIntervals plus the open interval. The STANDARD clock. */
  standardFulfilledFieldTime: number;
  pacingInterval: number;
  flexibility: number;
}

export interface IQuotaInputs {
  quotaId: string;
  quotaTitle: string | null;
  isActive: boolean;
  percentage: number;
  achieved: number;
  maximum: number;
  /** What the lambda last PATCHed. The prediction is checked against this. */
  currentTarget: number;
  currentlyOpen: number;
}

export interface IQuotaProjection extends IQuotaInputs {
  /** current_target the lambda would PATCH. */
  target: number;
  /** What suppliers would see: max(current_target - achieved, 0). */
  open: number;
  /** Open slots at zero while the survey is still short of its goal. */
  isIdle: boolean;
  /** The quota was opened by the falsy-target fallback, not by pacing. */
  isFallbackToMaximum: boolean;
  matchesStored: boolean;
}

export interface IPathProjection {
  projectTarget: number;
  quotas: IQuotaProjection[];
  totalOpen: number;
}

export interface IQuotaComparison {
  quotaId: string;
  quotaTitle: string | null;
  percentage: number;
  achieved: number;
  maximum: number;
  standard: IQuotaProjection;
  midway: IQuotaProjection;
  storedTarget: number;
  storedOpen: number;
  activeMatches: boolean;
}

/**
 * Standard path, cumulative (FEOT v4 section 3.1):
 *   POTF       = min((fftIntervalsSum + pacingInterval) / planned x 100, 100)
 *   projectCCT = floor((POTF + flexibility) x goal / 100)
 */
export function standardProjectTarget(inputs: IProjectionInputs): number {
  const { goal, plannedFieldTime, standardFulfilledFieldTime, pacingInterval } =
    inputs;
  const { flexibility } = inputs;
  if (!plannedFieldTime) {
    return 0;
  }
  const after = standardFulfilledFieldTime + pacingInterval;
  const percentOfTimeFulfilled = Math.min(
    (after / plannedFieldTime) * HUNDRED,
    HUNDRED
  );
  return Math.floor(((percentOfTimeFulfilled + flexibility) * goal) / HUNDRED);
}

/**
 * Midway path, one interval's increment over the remaining goal (FEOT v4
 * section 3.1). remainingTime carries feot.js's floor of one pacingInterval.
 */
export function midwayProjectTarget(inputs: IProjectionInputs): number {
  const { goal, fielded, plannedFieldTime, elapsedFieldTime, pacingInterval } =
    inputs;
  const remainingGoal = goal - fielded;
  const rawRemainingTime = plannedFieldTime - elapsedFieldTime;
  const remainingTime =
    rawRemainingTime < pacingInterval ? pacingInterval : rawRemainingTime;
  const intervalsRemaining = remainingTime / pacingInterval;
  if (!intervalsRemaining) {
    return 0;
  }
  return Math.round(remainingGoal / intervalsRemaining);
}

/**
 * updateQuotasCT, transcribed. The falsy-target fallback runs BEFORE the midway
 * `+ achieved`, so a zero target opens the quota to maximum on both paths
 * (FEOT v4 section 10.2, case G).
 */
export function projectQuota(
  quota: IQuotaInputs,
  projectTarget: number,
  isMidway: boolean
): IQuotaProjection {
  const percentage = quota.percentage ?? HUNDRED;
  const isFallbackToMaximum = !projectTarget;

  let target = isFallbackToMaximum
    ? quota.maximum
    : Math.round(projectTarget * (percentage / HUNDRED));

  if (isMidway) {
    target = target + quota.achieved;
    target = target > quota.maximum ? quota.maximum : target;
  }

  const open = Math.max(target - quota.achieved, 0);

  return {
    ...quota,
    percentage,
    target,
    open,
    isIdle: open === 0 && quota.achieved < quota.maximum,
    isFallbackToMaximum,
    matchesStored: target === quota.currentTarget,
  };
}

export function projectPath(
  inputs: IProjectionInputs,
  quotas: IQuotaInputs[],
  isMidway: boolean
): IPathProjection {
  const projectTarget = isMidway
    ? midwayProjectTarget(inputs)
    : standardProjectTarget(inputs);

  const projected = quotas
    .filter((quota) => quota.isActive)
    .map((quota) => projectQuota(quota, projectTarget, isMidway));

  return {
    projectTarget,
    quotas: projected,
    totalOpen: projected.reduce((sum, quota) => sum + quota.open, 0),
  };
}

/** Walks both projections together, one row per quota. */
export function compareProjections(
  standard: IPathProjection | null,
  midway: IPathProjection | null,
  isMidwayActive: boolean
): IQuotaComparison[] {
  if (!standard || !midway) {
    return [];
  }
  return standard.quotas.map((row, index) => {
    const midwayRow = midway.quotas[index];
    const active = isMidwayActive ? midwayRow : row;
    return {
      quotaId: row.quotaId,
      quotaTitle: row.quotaTitle,
      percentage: row.percentage,
      achieved: row.achieved,
      maximum: row.maximum,
      standard: row,
      midway: midwayRow,
      storedTarget: row.currentTarget,
      storedOpen: row.currentlyOpen,
      activeMatches: active.matchesStored,
    };
  });
}
